import _ from "lodash";
import React from "react";

import classes from "./UnitList.scss";
import { getAttachmentUrlFromFieldValue, onEnterOrSpaceKeyDown } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import classNames from "classnames";
import { CardSelection, EmptyState, RadioButton } from "@toddle-edu/ds-web";
import {
  NoUnitFoundIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";
import { getIsNewGoalTaggingEnabled } from "Post/utils";
import { getIsMultipleUnitTaggingEnabled } from "Post/utils";

const BLANK_OBJECT = {};

const styles = {
  newImagePlaceholder: {
    borderRadius: "8px",
    height: "48px",
    width: "48px",
    flex: "inherit",
  },
  imagePlaceholder: {
    height: "64px",
    width: "80px",
    flex: "inherit",
  },
  newImagePlaceholderV2: {
    height: "144px",
    width: "100%",
    flex: "inherit",
    borderRadius: "8px 8px 0 0",
  },
  emptyStateContainer: {
    minHeight: "600px",
  },
  selectionCardStyle: { height: "fit-content" },
};

const emptyStateSubtitleStyle = {
  lineHeight: "24px",
};

class UnitList extends React.PureComponent {
  constructor(props) {
    super(props);
    this.unitContainerRef = React.createRef();
  }

  componentDidMount = () => {
    if (this.unitContainerRef) {
      document.addEventListener("keydown", this.handleKeyPress);
    }
  };

  componentWillUnmount = () => {
    if (this.unitContainerRef) {
      document.removeEventListener("keydown", this.handleKeyPress);
    }
  };

  getElementNodes = containerRef => {
    if (!containerRef) {
      return [];
    }

    return Array.from(
      containerRef.querySelectorAll('div, [tabindex="0"]')
    ).filter(node => {
      return node.className.includes("singleUnitItemContainer");
    });
  };

  stopPropagationAndPreventDefault = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleKeyPress = event => {
    const { key } = event;
    const elementNodes = this.getElementNodes(this.unitContainerRef?.current);
    if (
      (key === "ArrowRight" || key === "d") &&
      !(document.activeElement.id === "search-unit")
    ) {
      this.stopPropagationAndPreventDefault(event);
      const currentIndex = elementNodes.indexOf(document.activeElement);
      if (currentIndex < elementNodes.length - 1) {
        elementNodes[currentIndex + 1]?.focus();
      } else {
        _.first(elementNodes)?.focus();
      }
    }
    if (
      (key === "ArrowLeft" || key === "a") &&
      !(document.activeElement.id === "search-unit")
    ) {
      this.stopPropagationAndPreventDefault(event);
      const currentIndex = elementNodes.indexOf(document.activeElement);
      if (currentIndex > 0) {
        elementNodes[currentIndex - 1]?.focus();
      } else {
        _.last(elementNodes)?.focus();
      }
    }
  };
  renderUnits = () => {
    const {
      props: {
        unitPlans,
        onItemClicked,
        selectedUnitId,
        searchText,
        t,
        curriculumProgramType,
        userType,
      },
    } = this;
    const isMultipleUnitTaggingEnabled = getIsMultipleUnitTaggingEnabled(
      userType
    );
    const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();
    const getGoalsAndStudentCount = unit => {
      return _.reduce(
        _.get(unit, "fields", []),
        (acc, element) => {
          const count = _.isArray(_.get(element, "value"))
            ? _.get(element, "value.length", 0)
            : 0;
          return acc + count;
        },
        0
      );
    };
    return _.get(unitPlans, "length", 0) > 0 ? (
      _.map(unitPlans, unit => {
        const planDetailStrings = [];
        const leCount = _.get(unit, "resourceLibrary.leCount", 0);
        const assessmentCount = _.get(
          unit,
          "resourceLibrary.assessmentCount",
          0
        );

        if (leCount > 0) {
          const leCountString = t("common:le_with_count", {
            count: leCount,
          });
          planDetailStrings.push(leCountString);
        }

        if (assessmentCount > 0) {
          const assessmentCountString = t("common:assessment_with_count", {
            count: assessmentCount,
          });
          planDetailStrings.push(assessmentCountString);
        }

        const isSelected = unit.id == selectedUnitId;

        const imageUrl = getAttachmentUrlFromFieldValue({
          fieldValue: _.get(unit, "image", ""),
        });

        const learningGoalsCount = getGoalsAndStudentCount(unit);

        const handleKeyDownUnitClick = e => {
          onEnterOrSpaceKeyDown(e, () => {
            onItemClicked(unit);
          });
        };
        const onUnitCardSelection = unit => () => {
          onItemClicked(unit);
        };
        return isNewGoalTaggingEnabled ? (
          <React.Fragment key={_.get(unit, "id")}>
            <CardSelection
              dsVersion="2.0"
              key={_.get(unit, "id")}
              type={isMultipleUnitTaggingEnabled ? "checkbox" : "radio"}
              image={
                <ResourceImagePlaceHolder
                  id={unit.id}
                  imageUrl={imageUrl}
                  imageThumbSize={220}
                  containerStyle={styles.newImagePlaceholderV2}
                />
              }
              value={_.get(unit, "id")}
              title={_.get(unit, "title.value") || "Untitled"}
              selectedValues={
                _.isArray(selectedUnitId) ? selectedUnitId : [selectedUnitId]
              }
              onCardSelectionClick={onUnitCardSelection(unit)}
              style={styles.selectionCardStyle}
              testId="unit-card-selection"
            />
          </React.Fragment>
        ) : (
          <React.Fragment key={_.get(unit, "id")}>
            <div
              tabIndex={0}
              className={classes.singleUnitItemContainer}
              key={_.get(unit, "id")}
              onClick={e => {
                onItemClicked(unit);
              }}
              data-test-id="journal-postcreation-tagunit-item"
              onKeyDown={handleKeyDownUnitClick}
            >
              <div className={classes.unitAvatar}>
                <ResourceImagePlaceHolder
                  id={unit.id}
                  imageUrl={imageUrl}
                  imageThumbSize={56}
                  containerStyle={styles.newImagePlaceholder}
                />
              </div>
              <div className={classes.unitDetailsContainer}>
                <div className={classes.unitTitle}>
                  {_.get(unit, "title.value") || "Untitled"}
                </div>
              </div>
              <RadioButton
                dsVersion="2.0"
                checked={selectedUnitId === _.get(unit, "id")}
                onClick={e => {
                  onItemClicked(unit);
                }}
                size="medium"
                tabIndex={-1}
              />
            </div>
          </React.Fragment>
        );
      })
    ) : _.get(searchText, "length", 0) > 0 ? (
      <div className={classes.noDataContainer}>
        <EmptyState
          illustration={NoSearchInFilterIllustration}
          title={t("common:noResultHeading")}
          subtitle={t("common:no_items_matching_your_search")}
          subtitleStyle={emptyStateSubtitleStyle}
          size="small"
        />
      </div>
    ) : (
      <div className={classes.noDataContainer}>
        <EmptyState
          illustration={NoUnitFoundIllustration}
          size="small"
          title={
            curriculumProgramType == "IB_PYP"
              ? t("journal:no_unit_scheduled")
              : t("journal:no_units")
          }
          subtitle={t("journal:create_unit_and_learning_goals")}
          subtitleStyle={emptyStateSubtitleStyle}
        />
      </div>
    );
  };

  render() {
    const {
      t,
      onSelectFromAllPYP,
      selectFromAllPYP,
      resourceListRef,
      unitPlans,
    } = this.props;

    const unitContainerClass = classNames({
      [classes.unitContainerSelected]: selectFromAllPYP,
      [classes.unitContainer]: true,
    });

    const listContentClasses = classNames({
      [classes.listContent]: true,
      [classes.listContentV2]: getIsNewGoalTaggingEnabled(),
    });
    return (
      <div className={classes.listContainer} ref={resourceListRef}>
        <div
          tabIndex={-1}
          ref={this.unitContainerRef}
          className={listContentClasses}
          style={
            _.isEmpty(unitPlans) ? styles.emptyStateContainer : BLANK_OBJECT
          }
        >
          {this.renderUnits()}
        </div>
      </div>
    );
  }
}

export default I18nHOC({ resource: ["common", "journal"] })(UnitList);
